import React, { useState } from "react";
import { Alert, Button, FormFeedback, Input, Label, Spinner } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Link, useParams } from "react-router-dom";

import ParticlesAuth from "./ParticlesAuth";
import { postResetPassword } from "../../helpers/backend_helper";

const extractErrorMessage = (payload: any) => {
  const data = payload?.response?.data ?? payload;

  if (typeof data === "string") return data;
  if (data?.message) return data.message;

  if (data?.errors && typeof data.errors === "object") {
    const firstKey = Object.keys(data.errors)[0];
    const firstError = data.errors[firstKey];

    if (Array.isArray(firstError)) return firstError[0];
    if (typeof firstError === "string") return firstError;
  }

  return "Đặt lại mật khẩu không thành công. Vui lòng thử lại.";
};

const ResetPassword = () => {
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      password_confirmation: "",
      token,
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Email không hợp lệ").required("Vui lòng nhập email"),
      password: Yup.string().min(8, "Mật khẩu tối thiểu 8 ký tự").required("Vui lòng nhập mật khẩu"),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password")], "Mật khẩu xác nhận không khớp")
        .required("Vui lòng nhập xác nhận mật khẩu"),
    }),
    onSubmit: async (values) => {
      if (!token) {
        setError("Liên kết đặt lại mật khẩu không hợp lệ.");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setSuccess(null);

        await postResetPassword({
          token,
          email: values.email,
          password: values.password,
          password_confirmation: values.password_confirmation,
        });

        setSuccess("Đặt lại mật khẩu thành công. Bạn có thể đăng nhập lại.");
      } catch (submitError: any) {
        setError(extractErrorMessage(submitError));
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = "Hệ thống quản lý đặt món - Đặt lại mật khẩu";

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-10 col-lg-8">
              <div className="text-center">
                <h2 className="text-white">Đặt lại mật khẩu</h2>
                <p className="text-white-50">Nhập email và mật khẩu mới để tiếp tục.</p>
              </div>

              <div className="register-form mt-4">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <Input type="hidden" name="token" value={token} />
                  {!token && (
                    <Alert color="danger">Liên kết đặt lại mật khẩu không hợp lệ hoặc đã hết hạn.</Alert>
                  )}
                  {error && <Alert color="danger">{error}</Alert>}
                  {success && <Alert color="success">{success}</Alert>}

                  <div className="form-group text-start mb-4">
                    <span>Địa chỉ Email</span>
                    <Label>
                      <i className="mdi mdi-email-outline"></i>
                    </Label>
                    <Input
                      name="email"
                      className="form-control"
                      placeholder="email@example.com"
                      type="email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      invalid={
                        !!(validation.touched.email && validation.errors.email)
                      }
                    />
                    {validation.touched.email && validation.errors.email ? (
                      <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="form-group text-start mb-4">
                    <span>Mật khẩu mới</span>
                    <Label>
                      <i className="mdi mdi-lock-outline"></i>
                    </Label>
                    <Input
                      name="password"
                      className="form-control"
                      placeholder="********"
                      type="password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password || ""}
                      invalid={
                        !!(validation.touched.password && validation.errors.password)
                      }
                    />
                    {validation.touched.password && validation.errors.password ? (
                      <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="form-group text-start mb-4">
                    <span>Xác nhận mật khẩu mới</span>
                    <Label>
                      <i className="mdi mdi-lock-check-outline"></i>
                    </Label>
                    <Input
                      name="password_confirmation"
                      className="form-control"
                      placeholder="********"
                      type="password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password_confirmation || ""}
                      invalid={
                        !!(
                          validation.touched.password_confirmation &&
                          validation.errors.password_confirmation
                        )
                      }
                    />
                    {validation.touched.password_confirmation &&
                    validation.errors.password_confirmation ? (
                      <FormFeedback type="invalid">
                        {validation.errors.password_confirmation}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <Button className="btn btn-warning btn-lg w-100" disabled={loading || !token} type="submit">
                    {loading && <Spinner size="sm" className="me-2" />}
                    Đặt lại mật khẩu
                  </Button>
                </form>
              </div>

              <div className="login-meta-data">
                <p className="mb-0 text-white">
                  <Link to="/login" className="text-warning">
                    Quay lại đăng nhập
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ResetPassword;
